/**
 * MultiViewLayout — a 2×2 grid of static orthographic panels shown above
 * the main interactive viewer when multi-view mode is active.
 *
 * Panels:
 *   - Front    (orthographic, looking from front: +Z world axis)
 *   - Top      (orthographic, looking down from above: +Y world axis)
 *   - Right    (orthographic, looking from right: +X world axis)
 *   - Wireframe (perspective, mirrors the main-view camera in real-time)
 *
 * No additional OpenSCAD compilation is needed — panels reuse the existing
 * THREE.BufferGeometry objects already parsed from the STL output.
 * Each <Canvas> re-uploads the vertex data to its own WebGL context, but
 * that is a cheap GPU-side upload with no CPU work.
 *
 * The wireframe panel receives the main-view camera state via a ref that is
 * updated every frame by a CameraSyncer component living inside the main Canvas.
 */

import {
  useEffect,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrthographicCamera } from '@react-three/drei';
import * as THREE from 'three';
import type { ColoredGeometry } from '@/components/viewer/types';
import { useColor } from '@/contexts/ColorContext';
import { SceneBackground } from '@/components/viewer/scene/SceneBackground';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type MultiViewCameraState = {
  position: THREE.Vector3;
  target: THREE.Vector3;
  quaternion: THREE.Quaternion;
  zoom: number;
};

export type MultiViewHandle = {
  /** Called each frame by the main viewer to sync the wireframe panel camera */
  syncCamera: (state: MultiViewCameraState) => void;
};

// ---------------------------------------------------------------------------
// Solid mesh for a single colored geometry (with stable material lifecycle)
// ---------------------------------------------------------------------------

/** ITU-R BT.709 luma coefficients — matches ModelRenderer. */
function toMultiViewLumaGray(color: THREE.Color): THREE.Color {
  const luma = 0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b;
  return new THREE.Color(luma, luma, luma);
}

function ColoredMeshItem({
  cg,
  grayscaleMode,
}: {
  cg: ColoredGeometry;
  grayscaleMode: boolean;
}) {
  const rawColor = useMemo(
    () =>
      cg.isGhost
        ? new THREE.Color(0.6, 0.6, 0.8)
        : new THREE.Color(cg.color[0], cg.color[1], cg.color[2]),
    [cg],
  );

  const meshColor = useMemo(
    () => (grayscaleMode ? toMultiViewLumaGray(rawColor) : rawColor),
    [rawColor, grayscaleMode],
  );

  const material = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: meshColor,
        opacity: cg.isGhost ? 0.3 : cg.color[3],
        transparent: cg.color[3] < 1 || cg.isGhost,
        roughness: grayscaleMode ? 1.0 : 0.5,
        metalness: grayscaleMode ? 0.0 : 0.1,
        envMapIntensity: grayscaleMode ? 0 : 1,
      }),
    [meshColor, cg.isGhost, cg.color, grayscaleMode],
  );

  useEffect(() => () => material.dispose(), [material]);

  return <mesh geometry={cg.geometry} material={material} />;
}

// ---------------------------------------------------------------------------
// Wireframe edges for a single colored geometry
// ---------------------------------------------------------------------------

function WireframeMeshItem({ cg }: { cg: ColoredGeometry }) {
  const edgesGeom = useMemo(
    () => new THREE.EdgesGeometry(cg.geometry, 15),
    [cg.geometry],
  );

  const material = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color: 'white',
        transparent: true,
        opacity: 0.8,
      }),
    [],
  );

  const lineSegments = useMemo(
    () => new THREE.LineSegments(edgesGeom, material),
    [edgesGeom, material],
  );

  useEffect(
    () => () => {
      edgesGeom.dispose();
      material.dispose();
    },
    [edgesGeom, material],
  );

  return <primitive object={lineSegments} />;
}

// ---------------------------------------------------------------------------
// Wireframe edges for a single geometry (monochrome path)
// ---------------------------------------------------------------------------

function SingleWireframeItem({ geometry }: { geometry: THREE.BufferGeometry }) {
  const edgesGeom = useMemo(
    () => new THREE.EdgesGeometry(geometry, 15),
    [geometry],
  );

  const material = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color: 'white',
        transparent: true,
        opacity: 0.8,
      }),
    [],
  );

  const lineSegments = useMemo(
    () => new THREE.LineSegments(edgesGeom, material),
    [edgesGeom, material],
  );

  useEffect(
    () => () => {
      edgesGeom.dispose();
      material.dispose();
    },
    [edgesGeom, material],
  );

  return <primitive object={lineSegments} />;
}

// ---------------------------------------------------------------------------
// Model renderer shared by all panels
// ---------------------------------------------------------------------------

function PanelModelRenderer({
  geometry,
  coloredGeometries,
  color,
  wireframe = false,
  grayscaleMode = false,
}: {
  geometry?: THREE.BufferGeometry;
  coloredGeometries: ColoredGeometry[];
  color: string;
  wireframe?: boolean;
  grayscaleMode?: boolean;
}) {
  if (coloredGeometries.length > 0) {
    return (
      <group rotation={[-Math.PI / 2, 0, 0]}>
        {coloredGeometries.map((cg) =>
          wireframe ? (
            <WireframeMeshItem key={cg.geometry.uuid} cg={cg} />
          ) : (
            <ColoredMeshItem
              key={cg.geometry.uuid}
              cg={cg}
              grayscaleMode={grayscaleMode}
            />
          ),
        )}
      </group>
    );
  }

  if (geometry) {
    return (
      <group rotation={[-Math.PI / 2, 0, 0]}>
        {wireframe ? (
          <SingleWireframeItem geometry={geometry} />
        ) : (
          <mesh geometry={geometry}>
            <meshStandardMaterial
              color={
                grayscaleMode
                  ? (() => {
                      const c = new THREE.Color(color);
                      const l = 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
                      return new THREE.Color(l, l, l);
                    })()
                  : color
              }
              roughness={grayscaleMode ? 1.0 : 0.5}
              metalness={grayscaleMode ? 0.0 : 0.1}
              envMapIntensity={grayscaleMode ? 0 : 1}
            />
          </mesh>
        )}
      </group>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Camera auto-fit for static orthographic panels (runs inside Canvas)
// ---------------------------------------------------------------------------

function OrthographicFitter({
  geometry,
  coloredGeometries,
  direction,
}: {
  geometry?: THREE.BufferGeometry;
  coloredGeometries: ColoredGeometry[];
  direction: 'front' | 'top' | 'right';
}) {
  const { camera, size, invalidate } = useThree();
  const needsFit = useRef(true);

  useEffect(() => {
    needsFit.current = true;
    invalidate();
  }, [geometry, coloredGeometries, size, invalidate]);

  useFrame(() => {
    if (!needsFit.current) return;
    needsFit.current = false;

    // Build overall bounding box.
    // Geometries are stored in their raw coordinate system and rendered with
    // a [-π/2, 0, 0] rotation group, so apply that rotation to get world-space bounds.
    const box = new THREE.Box3();
    const rotM = new THREE.Matrix4().makeRotationX(-Math.PI / 2);

    const addGeom = (geom: THREE.BufferGeometry) => {
      geom.computeBoundingBox();
      if (!geom.boundingBox) return;
      const b = geom.boundingBox.clone().applyMatrix4(rotM);
      box.union(b);
    };

    if (coloredGeometries.length > 0) {
      coloredGeometries.forEach((cg) => addGeom(cg.geometry));
    } else if (geometry) {
      addGeom(geometry);
    }

    if (box.isEmpty()) return;

    const center = new THREE.Vector3();
    box.getCenter(center);
    const bsize = new THREE.Vector3();
    box.getSize(bsize);

    const cam = camera as THREE.OrthographicCamera;
    const DIST = 500;
    const MARGIN = 1.25;

    // The canvas aspect ratio — must be factored in so the frustum is never
    // stretched. We choose a "fit half-size" that covers the object in the
    // view-plane, then derive horizontal/vertical extents from aspect ratio.
    const aspect = size.width / size.height;

    // Helper: given the object's visible width (w) and height (h) in the
    // view plane, compute frustum half-extents that contain both dimensions
    // while maintaining the canvas aspect ratio (no distortion).
    const fitFrustum = (objW: number, objH: number) => {
      const objAspect = objW / objH;
      let halfW: number;
      let halfH: number;
      if (objAspect > aspect) {
        // Object is wider relative to canvas — fit to width
        halfW = (objW * MARGIN) / 2;
        halfH = halfW / aspect;
      } else {
        // Object is taller relative to canvas — fit to height
        halfH = (objH * MARGIN) / 2;
        halfW = halfH * aspect;
      }
      return { halfW, halfH };
    };

    if (direction === 'front') {
      // Looking from +Z: visible plane is X (width) × Y (height)
      const { halfW, halfH } = fitFrustum(bsize.x, bsize.y);
      cam.position.set(center.x, center.y, center.z + DIST);
      cam.up.set(0, 1, 0);
      cam.left = -halfW;
      cam.right = halfW;
      cam.top = halfH;
      cam.bottom = -halfH;
    } else if (direction === 'top') {
      // Looking from +Y down: visible plane is X (width) × Z (height)
      const { halfW, halfH } = fitFrustum(bsize.x, bsize.z);
      cam.position.set(center.x, center.y + DIST, center.z);
      cam.up.set(0, 0, -1);
      cam.left = -halfW;
      cam.right = halfW;
      cam.top = halfH;
      cam.bottom = -halfH;
    } else if (direction === 'right') {
      // Looking from +X: visible plane is Z (width) × Y (height)
      const { halfW, halfH } = fitFrustum(bsize.z, bsize.y);
      cam.position.set(center.x + DIST, center.y, center.z);
      cam.up.set(0, 1, 0);
      cam.left = -halfW;
      cam.right = halfW;
      cam.top = halfH;
      cam.bottom = -halfH;
    }

    cam.lookAt(center);
    cam.updateProjectionMatrix();
    invalidate();
  });

  return null;
}

// ---------------------------------------------------------------------------
// Wireframe panel camera sync (mirrors the main camera each frame)
// ---------------------------------------------------------------------------

function WireframeCameraSync({
  cameraStateRef,
}: {
  cameraStateRef: React.RefObject<MultiViewCameraState | null>;
}) {
  const { camera, invalidate } = useThree();

  useFrame(() => {
    const state = cameraStateRef.current;
    if (!state) return;
    camera.position.copy(state.position);
    camera.quaternion.copy(state.quaternion);
    camera.updateMatrixWorld();
    invalidate();
  });

  return null;
}

// ---------------------------------------------------------------------------
// Label overlay
// ---------------------------------------------------------------------------

function PanelLabel({ label }: { label: string }) {
  return (
    <div className="pointer-events-none absolute left-2 top-2 select-none rounded bg-background/50 px-1.5 py-0.5 text-xs font-medium text-foreground/70 backdrop-blur-sm">
      {label}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Divider lines between panels
// ---------------------------------------------------------------------------

function PanelDividers() {
  return (
    <>
      {/* Vertical center divider */}
      <div className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-px bg-border/60" />
      {/* Horizontal center divider */}
      <div className="pointer-events-none absolute inset-x-0 top-1/2 h-px -translate-y-px bg-border/60" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Static orthographic panel (front / top / right)
// ---------------------------------------------------------------------------

/** Studio lighting identical to the main viewer's default setup. */
function PanelStudioLighting() {
  return (
    <>
      <ambientLight intensity={1.0} />
      {/* Key light */}
      <directionalLight position={[10, 15, 10]} intensity={1.5} />
      {/* Fill light */}
      <directionalLight position={[-8, 8, -5]} intensity={0.5} />
      {/* Rim / back light */}
      <directionalLight position={[0, 5, -15]} intensity={0.4} />
      {/* Bottom fill */}
      <directionalLight position={[0, -5, 0]} intensity={0.2} />
    </>
  );
}

function StaticPanel({
  direction,
  geometry,
  coloredGeometries,
  color,
  backgroundColor,
  grayscaleMode = false,
}: {
  direction: 'front' | 'top' | 'right';
  geometry?: THREE.BufferGeometry;
  coloredGeometries: ColoredGeometry[];
  color: string;
  backgroundColor: string;
  grayscaleMode?: boolean;
}) {
  const label =
    direction === 'front' ? 'Front' : direction === 'top' ? 'Top' : 'Right';

  return (
    <div className="relative h-full w-full overflow-hidden">
      <Canvas frameloop="demand" linear dpr={[1, 2]} gl={{ antialias: true }}>
        <OrthographicCamera
          makeDefault
          position={[0, 0, 500]}
          near={0.01}
          far={5000}
          zoom={1}
        />

        <SceneBackground color={backgroundColor} />
        <PanelStudioLighting />

        <OrthographicFitter
          geometry={geometry}
          coloredGeometries={coloredGeometries}
          direction={direction}
        />

        <PanelModelRenderer
          geometry={geometry}
          coloredGeometries={coloredGeometries}
          color={color}
          grayscaleMode={grayscaleMode}
        />
      </Canvas>
      <PanelLabel label={label} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wireframe panel — perspective, follows main camera
// ---------------------------------------------------------------------------

function WireframePanel({
  geometry,
  coloredGeometries,
  color,
  backgroundColor,
  cameraStateRef,
}: {
  geometry?: THREE.BufferGeometry;
  coloredGeometries: ColoredGeometry[];
  color: string;
  backgroundColor: string;
  cameraStateRef: React.RefObject<MultiViewCameraState | null>;
}) {
  return (
    <div className="relative h-full w-full overflow-hidden">
      <Canvas
        frameloop="demand"
        linear
        dpr={[1, 2]}
        gl={{ antialias: true }}
        camera={{ position: [-100, 100, 100], fov: 45, near: 0.1, far: 5000 }}
      >
        <SceneBackground color={backgroundColor} />
        <WireframeCameraSync cameraStateRef={cameraStateRef} />
        <PanelModelRenderer
          geometry={geometry}
          coloredGeometries={coloredGeometries}
          color={color}
          wireframe
        />
      </Canvas>
      <PanelLabel label="Wireframe" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public MultiViewLayout component
// ---------------------------------------------------------------------------

interface MultiViewLayoutProps {
  geometry?: THREE.BufferGeometry;
  coloredGeometries: ColoredGeometry[];
  backgroundColor: string;
  grayscaleMode?: boolean;
}

export const MultiViewLayout = forwardRef<
  MultiViewHandle,
  MultiViewLayoutProps
>(function MultiViewLayout(
  { geometry, coloredGeometries, backgroundColor, grayscaleMode = false },
  ref,
) {
  const { color } = useColor();

  // Shared camera state ref — written by main viewer, read by wireframe panel
  const cameraStateRef = useRef<MultiViewCameraState | null>(null);

  const syncCamera = useCallback((state: MultiViewCameraState) => {
    if (!cameraStateRef.current) {
      cameraStateRef.current = {
        position: state.position.clone(),
        target: state.target.clone(),
        quaternion: state.quaternion.clone(),
        zoom: state.zoom,
      };
    } else {
      cameraStateRef.current.position.copy(state.position);
      cameraStateRef.current.target.copy(state.target);
      cameraStateRef.current.quaternion.copy(state.quaternion);
      cameraStateRef.current.zoom = state.zoom;
    }
  }, []);

  useImperativeHandle(ref, () => ({ syncCamera }), [syncCamera]);

  return (
    <div className="relative grid h-full w-full grid-cols-2 grid-rows-2">
      <StaticPanel
        direction="front"
        geometry={geometry}
        coloredGeometries={coloredGeometries}
        color={color}
        backgroundColor={backgroundColor}
        grayscaleMode={grayscaleMode}
      />
      <StaticPanel
        direction="top"
        geometry={geometry}
        coloredGeometries={coloredGeometries}
        color={color}
        backgroundColor={backgroundColor}
        grayscaleMode={grayscaleMode}
      />
      <StaticPanel
        direction="right"
        geometry={geometry}
        coloredGeometries={coloredGeometries}
        color={color}
        backgroundColor={backgroundColor}
        grayscaleMode={grayscaleMode}
      />
      <WireframePanel
        geometry={geometry}
        coloredGeometries={coloredGeometries}
        color={color}
        backgroundColor={backgroundColor}
        cameraStateRef={cameraStateRef}
      />

      {/* Thin dividers between the four quadrants */}
      <PanelDividers />
    </div>
  );
});
